import type {
    ExportSettingsSourceType,
    PredefinedValueType,
} from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

export type CrmProvider = Integration;

export type CrmEnrichmentJobType = "instant" | "scheduled";

export type CrmEnrichmentJobObject = "company" | "contact" | "lead";

export type CrmEnrichmentJobStatus = "created" | "inProgress" | "failed" | "success";

export type CrmEnrichmentJobRunOutcome = "succeeded" | "partiallyFailed" | "failed";

export type CrmEnrichmentSource = "website" | "company";

export type CrmEnrichmentJobRefreshPeriod = "never" | "asSoonAsPossible" | "twoMonths" | "threeMonth" | "sixMonth";

export type LeadScoreRefreshDataInterval = "daily" | "weekly" | "monthly";

export type CrmEnrichmentJobTabName = "type" | "settings" | "filters" | "fields-mapping";

export type CrmEnrichmentJobFilterDTO = {
    readonly fieldName: string;
    readonly fieldValues: string;
    readonly operator: Operator;
};

export type EnrichmentJobMappingDTO = {
    readonly externalField: string;
    readonly overwrite: boolean;
    readonly sourceType?: ExportSettingsSourceType;
    readonly metadata?: string;
};

export type CrmEnrichmentJobDTO = {
    readonly name: string;
    readonly enrichmentObject: CrmEnrichmentJobObject;
    readonly type: CrmEnrichmentJobType;
    readonly country: number;
    readonly refreshDataInterval: CrmEnrichmentJobRefreshPeriod;
    readonly leadScoreRefreshDataInterval?: LeadScoreRefreshDataInterval;
    readonly filters: CrmEnrichmentJobFilterDTO[];
    readonly enrichmentSource: CrmEnrichmentSource;
    readonly mappings: readonly EnrichmentJobMappingDTO[];
};

export type CrmEnrichmentJob = CrmEnrichmentJobDTO & {
    readonly id: string;
    readonly active: boolean;
    readonly lastRunDate?: string | null;
    readonly lastRunId?: string | null;
    readonly frequencyInMinutes?: number | null;
    readonly status: CrmEnrichmentJobStatus | null;
    readonly lastRunOutcome?: CrmEnrichmentJobRunOutcome | null;
};

export type CrmEnrichmentJobToggleDTO = {
    readonly jobId: string;
    readonly value: boolean;
};

export type CrmEnrichmentJobRenameDTO = {
    readonly jobId: string;
    readonly name: string;
};

export type Operator = "equals" | "notEquals" | "contains" | "notContains" | "greater" | "lower" | "empty" | "notEmpty";

export type CrmEnrichmentJobPriceDto = readonly {
    readonly metadata: string;
    readonly sourceType: number;
}[];

export type CrmEnrichmentJobPrice = {
    readonly price: number;
};

export enum CrmEnrichmentWizardSteps {
    TypeStep,
    SettingsStep,
    FiltersStep,
    FieldsMappingStep,
    ReviewStep,
}

export type CrmEnrichmentJobRunHistoryItem = {
    readonly runId: string;
    readonly name: string;
    readonly runDate: string;
    readonly totalRecords: number | null;
    readonly succeeded: number | null;
    readonly failed: number | null;
    readonly credits: number | null;
    readonly externalId?: string | null;
    readonly hasLogsFile?: boolean;
};

export type CrmEnrichmentJobRunHistoryResponse = {
    readonly totalCount: number;
    readonly page: number | null;
    readonly pageSize: number | null;
    readonly records: readonly CrmEnrichmentJobRunHistoryItem[];
};

export type CrmEnrichmentJobRunHistoryParams = {
    readonly page: number;
    readonly pageSize: number;
};

export type CrmEnrichmentJobFilterType = {
    readonly id: number;
    readonly field: string;
    readonly operator: Operator | "";
    readonly multiValue: readonly string[];
    readonly singleValue: string;
    readonly selectedMultiValues: Readonly<Record<string, boolean>>;
    readonly dataType: PredefinedValueType;
    readonly pickListItems: readonly string[];
};
